/**
 * Bulk Broadcast Service
 * Handles mass messaging operations for different channels
 */

/* global axios */

import { useAlert } from 'dashboard/composables';

class BulkBroadcastService {
  /**
   * Get the current account ID from the route
   */
  get accountId() {
    const isInsideAccountScopedURLs = window.location.pathname.includes('/app/accounts');
    if (isInsideAccountScopedURLs) {
      return window.location.pathname.split('/')[3];
    }
    return '';
  }

  /**
   * Send WhatsApp template messages to multiple recipients
   * @param {Array} payload - Array of message objects
   * @param {Object} options - { scheduledAt } ISO date string para envíos programados
   * @returns {Promise} - API response
   */
  async sendWhatsAppTemplateBatch(payload, { scheduledAt = null } = {}) {
    try {
      const url = `api/v1/accounts/${this.accountId}/bulk/whatsapp/send`;
      
      const body = {
        messages: payload,
        async: payload.length > 10 // Use async for large batches
      };

      // Presente sólo si el envío es programado; ausente = disparo inmediato.
      if (scheduledAt) {
        body.scheduled_at = scheduledAt;
      }

      const response = await axios.post(url, body);

      return response.data;
    } catch (error) {
      console.error('Error sending WhatsApp bulk messages:', error);
      throw error;
    }
  }

  /**
   * Send bulk email messages
   * @param {Array} payload - Array of email objects
   * @returns {Promise} - API response
   */
  async sendEmailBatch(payload) {
    try {
      const url = `api/v1/accounts/${this.accountId}/bulk/email/send`;
      
      const response = await axios.post(url, { 
        messages: payload 
      });

      return response.data;
    } catch (error) {
      console.error('Error sending email bulk messages:', error);
      throw error;
    }
  }

  /**
   * Send bulk SMS messages
   * @param {Array} payload - Array of SMS objects
   * @returns {Promise} - API response
   */
  async sendSMSBatch(payload) {
    try {
      const url = `api/v1/accounts/${this.accountId}/bulk/sms/send`;
      
      const response = await axios.post(url, { 
        messages: payload 
      });

      return response.data;
    } catch (error) {
      console.error('Error sending SMS bulk messages:', error);
      throw error;
    }
  }

  /**
   * Generic bulk send method that routes to appropriate channel service
   * @param {string} channel - The channel type (whatsapp, email, sms)
   * @param {Array} payload - Array of message objects
   * @param {Object} options - { scheduledAt } ISO date string para envíos programados
   * @returns {Promise} - API response
   */
  async sendBulkMessages(channel, payload, options = {}) {
    switch (channel) {
      case 'whatsapp':
        return this.sendWhatsAppTemplateBatch(payload, options);
      case 'email':
        return this.sendEmailBatch(payload);
      case 'sms':
        return this.sendSMSBatch(payload);
      default:
        throw new Error(`Unsupported channel: ${channel}`);
    }
  }

  /**
   * Validate bulk message payload
   * @param {string} channel - The channel type
   * @param {Array} payload - Array of message objects
   * @returns {Object} - Validation result
   */
  validatePayload(channel, payload) {
    const errors = [];

    if (!Array.isArray(payload) || payload.length === 0) {
      errors.push('Payload must be a non-empty array');
      return { isValid: false, errors };
    }

    payload.forEach((message, index) => {
      switch (channel) {
        case 'whatsapp':
          if (!message.to || !message.template_id || !message.language) {
            errors.push(`Message ${index + 1}: Missing required fields (to, template_id, language)`);
          }
          if (message.variables && !Array.isArray(message.variables)) {
            errors.push(`Message ${index + 1}: Variables must be an array`);
          }
          break;

        case 'email':
          if (!message.to || !message.subject || !message.body) {
            errors.push(`Message ${index + 1}: Missing required fields (to, subject, body)`);
          }
          break;

        case 'sms':
          if (!message.to || !message.body) {
            errors.push(`Message ${index + 1}: Missing required fields (to, body)`);
          }
          break;

        default:
          errors.push(`Unsupported channel: ${channel}`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get estimated cost for bulk sending (if pricing API is available)
   * @param {string} channel - The channel type
   * @param {number} messageCount - Number of messages
   * @returns {Promise} - Cost estimation
   */
  async getEstimatedCost(channel, messageCount) {
    try {
      // TODO: Implement when pricing API is available
      const response = await fetch(`/api/v1/bulk/${channel}/cost-estimate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message_count: messageCount })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting cost estimate:', error);
      // Return default estimation or null if API is not available
      return null;
    }
  }

  /**
   * Get sending limits for the current account/channel
   * @param {string} channel - The channel type
   * @returns {Promise} - Limits information
   */
  async getSendingLimits(channel) {
    try {
      // TODO: Implement when limits API is available
      const response = await fetch(`/api/v1/bulk/${channel}/limits`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting sending limits:', error);
      // Return default limits if API is not available
      return {
        daily_limit: 1000,
        monthly_limit: 10000,
        current_daily_usage: 0,
        current_monthly_usage: 0
      };
    }
  }
}

// Export singleton instance
export default new BulkBroadcastService();